import type { OmniboxDemo, OmniboxDemoV2 } from "@mw/shared/payload-types";
import type { Graph, Organization, Thing, WebApplication, WithContext } from "schema-dts";

import { getAppleAppRating } from "@/app/_data/productRatings";
import { serializeJsonLd } from "@utilities/schema/serialize";

// WebApplication (subtype of SoftwareApplication) instead of Product to avoid
// triggering Google's Merchant Listing validator, which requires shipping/return
// policy fields that don't apply to SaaS.
//
// Async builders (vs static consts) so the aggregateRating refreshes daily from
// the iTunes Search API instead of drifting stale. Cached for 24h via Next.js
// `fetch` revalidation, so calling these is effectively free at render time.

// Product-specific imagery so Google's Software App rich result shows the
// correct screenshot for each product. Sharing one array between TTS and STT
// would attribute STT screenshots to the TTS product page.
const TTS_IMAGES = [
  "https://eleven-public-cdn.elevenlabs.io/payloadcms/1nxnhayxxlm-text-to-speech-ai-voice-generator.webp",
  "https://eleven-public-cdn.elevenlabs.io/payloadcms/4ffy4qj3q58-text-to-speech-free-read-aloud.webp",
];

const STT_IMAGES = [
  "https://eleven-public-cdn.elevenlabs.io/payloadcms/8rt2oztdckk-speech-to-text-1x1.webp",
  "https://eleven-public-cdn.elevenlabs.io/payloadcms/9ezz1faor19-speech-to-text-4x3.webp",
  "https://eleven-public-cdn.elevenlabs.io/payloadcms/oy2xaupx3c-speech-to-text-16x9.webp",
];

const PRODUCT_OFFERS = {
  "@type": "Offer",
  price: "0",
  priceCurrency: "USD",
  availability: "https://schema.org/OnlineOnly",
  url: "https://elevenlabs.io/pricing",
} as const;

export const baseOrgStructuredData: Organization = {
  "@type": "Organization",
  name: "ElevenLabs",
  url: "https://elevenlabs.io",
};

export async function getTtsStructuredData(): Promise<WithContext<WebApplication>> {
  const aggregateRating = await getAppleAppRating();
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "ElevenLabs Text to Speech",
    description:
      "Generate natural, expressive AI voices in 70+ languages from text. Use ElevenLabs Text to Speech for podcasts, audiobooks, video voiceovers, and accessibility.",
    url: "https://elevenlabs.io/text-to-speech",
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    image: TTS_IMAGES,
    offers: PRODUCT_OFFERS,
    aggregateRating: { "@type": "AggregateRating", ...aggregateRating },
  };
}

export async function getSttStructuredData(): Promise<WithContext<WebApplication>> {
  const aggregateRating = await getAppleAppRating();
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "ElevenLabs Speech to Text",
    description: "High quality, Speech to Text transcription in 99 languages.",
    url: "https://elevenlabs.io/speech-to-text",
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    image: STT_IMAGES,
    offers: PRODUCT_OFFERS,
    aggregateRating: { "@type": "AggregateRating", ...aggregateRating },
  };
}

// Async server-component wrappers so sync parent components can drop in
// `<TtsSchemaTag />` without becoming async themselves. Next.js streams the
// awaited result into the initial HTML.
export async function TtsSchemaTag() {
  const data = await getTtsStructuredData();
  return <StructuredData data={[data]} id='tts-structured-data' />;
}

export async function SttSchemaTag() {
  const data = await getSttStructuredData();
  return <StructuredData data={[data]} id='stt-structured-data' />;
}

// Demo types that gate `<TtsSchemaTag />`. Colocated with the tag so any new
// TTS-rendering demo stays in sync with the schema. V1 and V2 are separate
// because they expose different field names and narrower type unions.
// The sibling `hasSTT` checks are intentionally inlined: each Omnibox variant
// accepts a different STT subset (Omnibox/OmniboxV2: `stt`; OmniboxSimple:
// `stt | stt-realtime`), so a shared constant would be wrong.
export const TTS_DEMO_COMPONENTS: OmniboxDemo["component"][] = ["tts", "voices-with-tts", "sliderGenerateTTS"];
export const TTS_DEMO_TEMPLATES: OmniboxDemoV2["template"][] = ["tts", "voices-with-tts"];

export const StructuredData = ({ id, data }: { data: Thing[]; id: string }) => {
  const graph: Graph = {
    "@context": "https://schema.org",
    "@graph": data,
  };
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: serializeJsonLd(graph),
      }}
      id={id}
      type='application/ld+json'
    />
  );
};
